import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  ArrowRight as ArrowForwardIcon,
  ArrowUp,
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Flame,
  Grid3X3,
  GraduationCap,
  Library,
  List,
  Monitor,
  MonitorPlay,
  Rocket as RocketIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Video,
  X,
  Funnel,
} from "lucide-react";
import "./Products.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSelectedProduct } from "../../api/reducers/selectedProductReducer";
import {
  getAllProductCategories,
  getAllValidProducts,
  getInterviewProducts,
  getValidProducts,
  getUpcomingProducts,
} from "../../api/action/products";
import { findPartnerCompanies } from "../../api/action/company";
import { motion } from "framer-motion";
import FormatByOperatingLocation from "../../components/FormatByOperatingLocation";
import {
  getEducationPageCacheKey,
  readEducationPageCache,
  writeEducationPageCache,
} from "../../domains/education/utils/educationPageCache";

const PRODUCTS_CACHE_MAX_AGE_MS = 60 * 1000;
const COURSES_PER_PAGE = 8;

const isInterviewProduct = (product = {}) => (
  String(product.productCategorySubType || product.product?.productCategorySubType || '').toUpperCase() === 'INTERVIEW'
);

const getCategoryParamId = () => {
  const categoryParam = new URLSearchParams(window.location.search).get('category');
  const categoryId = Number(categoryParam);
  return !Number.isNaN(categoryId) && categoryId > 0 ? categoryId : null;
};

const spacingValue = (value) => (typeof value === "number" ? `${value * 8}px` : value);

const responsiveValue = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  if ("xs" in value || "sm" in value || "md" in value || "lg" in value || "xl" in value) {
    return value.xs ?? value.sm ?? value.md ?? value.lg ?? value.xl;
  }
  return value;
};

const sxToStyle = (sx = {}) => {
  const style = {};
  Object.entries(sx || {}).forEach(([key, rawValue]) => {
    if (key.startsWith("&")) return;
    const value = responsiveValue(rawValue);
    if (value && typeof value === "object" && !Array.isArray(value)) return;

    if (key === "p") style.padding = spacingValue(value);
    else if (key === "px") {
      style.paddingLeft = spacingValue(value);
      style.paddingRight = spacingValue(value);
    } else if (key === "py") {
      style.paddingTop = spacingValue(value);
      style.paddingBottom = spacingValue(value);
    } else if (key === "pt") style.paddingTop = spacingValue(value);
    else if (key === "pb") style.paddingBottom = spacingValue(value);
    else if (key === "pl") style.paddingLeft = spacingValue(value);
    else if (key === "pr") style.paddingRight = spacingValue(value);
    else if (key === "m") style.margin = spacingValue(value);
    else if (key === "mx") {
      style.marginLeft = spacingValue(value);
      style.marginRight = spacingValue(value);
    } else if (key === "my") {
      style.marginTop = spacingValue(value);
      style.marginBottom = spacingValue(value);
    } else if (key === "mt") style.marginTop = spacingValue(value);
    else if (key === "mb") style.marginBottom = spacingValue(value);
    else if (key === "ml") style.marginLeft = spacingValue(value);
    else if (key === "mr") style.marginRight = spacingValue(value);
    else if (key === "gap") style.gap = spacingValue(value);
    else if (key === "rowGap") style.rowGap = spacingValue(value);
    else if (key === "columnGap") style.columnGap = spacingValue(value);
    else style[key] = value;
  });
  return style;
};

const alpha = (color, opacity) => {
  if (!color?.startsWith?.("#")) return color;
  const hex = color.replace("#", "");
  const value = hex.length === 3
    ? hex.split("").map((part) => part + part).join("")
    : hex;
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const appTheme = {
  palette: { primary: { main: "#746EAC" } },
  breakpoints: {
    down: (key) => `(max-width: ${key === "sm" ? 640 : 768}px)`,
  },
};

const useTheme = () => appTheme;

const useMediaQuery = (query) => {
  const getMatches = () => (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    mediaQueryList.addEventListener?.("change", handleChange);
    return () => mediaQueryList.removeEventListener?.("change", handleChange);
  }, [query]);

  return matches;
};

const mergeStyles = (sx, style) => ({ ...sxToStyle(sx), ...(style || {}) });

const Box = ({ children, sx, style, component: Component = "div", ...props }) => (
  <Component style={mergeStyles(sx, style)} {...props}>{children}</Component>
);

const Card = ({ children, sx, style, ...props }) => (
  <div style={mergeStyles(sx, style)} {...props}>{children}</div>
);

const CardContent = ({ children, sx, style, ...props }) => (
  <div style={mergeStyles(sx, style)} {...props}>{children}</div>
);

const CardMedia = ({ image, alt, sx, style, component, ...props }) => (
  <img src={image} alt={alt} style={mergeStyles(sx, style)} {...props} />
);

const Container = ({ children, maxWidth = "lg", sx, style, ...props }) => {
  const widths = { md: "768px", lg: "1024px", xl: "1280px" };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: widths[maxWidth] || widths.lg,
        marginLeft: "auto",
        marginRight: "auto",
        paddingLeft: "16px",
        paddingRight: "16px",
        ...mergeStyles(sx, style),
      }}
      {...props}
    >
      {children}
    </div>
  );
};

const Typography = ({ children, variant, sx, style, ...props }) => {
  const Component = variant?.startsWith?.("h") ? variant : variant === "caption" ? "span" : "p";
  return <Component style={mergeStyles(sx, style)} {...props}>{children}</Component>;
};

const Button = ({ children, fullWidth, startIcon, sx, style, type = "button", variant, size, ...props }) => (
  <button
    type={type}
    style={{
      width: fullWidth ? "100%" : undefined,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      border: "1px solid transparent",
      cursor: props.disabled ? "not-allowed" : "pointer",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {startIcon}
    {children}
  </button>
);

const IconButton = ({ children, sx, style, type = "button", size, ...props }) => (
  <button
    type={type}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: 0,
      background: "transparent",
      cursor: "pointer",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {children}
  </button>
);

const Chip = ({ icon, label, onDelete, sx, style, size, ...props }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      whiteSpace: "nowrap",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {icon}
    <span>{label}</span>
    {onDelete && (
      <button type="button" aria-label={`Remove ${label}`} onClick={onDelete} style={{ border: 0, background: "transparent", cursor: "pointer", color: "inherit" }}>
        x
      </button>
    )}
  </span>
);

const Drawer = ({ children, open, onClose, PaperProps = {} }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose}>
      <div
        className="h-full bg-white shadow-xl"
        style={mergeStyles(PaperProps.sx, PaperProps.style)}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const Checkbox = ({ sx, style, size, ...props }) => (
  <input type="checkbox" style={mergeStyles(sx, style)} {...props} />
);

const FormControlLabel = ({ control, label, sx, style, ...props }) => (
  <label style={{ display: "flex", alignItems: "center", gap: "8px", ...mergeStyles(sx, style) }} {...props}>
    {control}
    {label}
  </label>
);

const FormGroup = ({ children, sx, style, ...props }) => (
  <div style={mergeStyles(sx, style)} {...props}>{children}</div>
);

const InputAdornment = ({ children }) => <span className="inline-flex items-center">{children}</span>;

const TextField = ({ inputRef, InputProps = {}, fullWidth, sx, style, size, ...props }) => (
  <div
    style={{
      width: fullWidth ? "100%" : undefined,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      padding: "6px 10px",
      ...mergeStyles(sx, style),
    }}
  >
    {InputProps.startAdornment}
    <input ref={inputRef} style={{ flex: 1, border: 0, outline: 0, minWidth: 0, font: "inherit" }} {...props} />
    {InputProps.endAdornment}
  </div>
);

const textFromChildren = (children) => {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textFromChildren).join(" ").trim();
  if (React.isValidElement(children)) return textFromChildren(children.props.children);
  return "";
};

const FormControl = ({ children, fullWidth, sx, style, size, ...props }) => (
  <div style={{ width: fullWidth ? "100%" : undefined, ...mergeStyles(sx, style) }} {...props}>{children}</div>
);

const Select = ({ children, sx, style, IconComponent, ...props }) => (
  <select style={mergeStyles(sx, style)} {...props}>{children}</select>
);

const MenuItem = ({ children, value }) => <option value={value}>{textFromChildren(children)}</option>;

const Paper = ({ children, sx, style, ...props }) => (
  <div style={mergeStyles(sx, style)} {...props}>{children}</div>
);

const Collapse = ({ children, in: isOpen }) => (isOpen ? <div>{children}</div> : null);

const styled = (BaseComponent) => (styleFactory) => {
  const StyledComponent = ({ sx, ...props }) => {
    const generatedStyle = typeof styleFactory === "function" ? styleFactory({ theme: appTheme }) : styleFactory;
    return <BaseComponent sx={{ ...generatedStyle, ...sx }} {...props} />;
  };
  return StyledComponent;
};

const createIcon = (Icon) => ({ sx, style, ...props }) => (
  <Icon style={mergeStyles(sx, style)} {...props} />
);

const Close = createIcon(X);
const Search = createIcon(SearchIcon);
const KeyboardArrowDown = createIcon(ChevronDown);
const FilterAlt = createIcon(Funnel);
const ViewModule = createIcon(Grid3X3);
const ViewList = createIcon(List);
const ExpandMore = createIcon(ChevronDown);
const ExpandLess = createIcon(ChevronUp);
const Computer = createIcon(Monitor);
const VideoLabel = createIcon(Video);
const Class = createIcon(MonitorPlay);
const ArrowForward = createIcon(ArrowForwardIcon);
const School = createIcon(SchoolIcon);
const AutoStories = createIcon(BookOpen);
const CastForEducation = createIcon(GraduationCap);
const TrendingUp = createIcon(TrendingUpIcon);
const Whatshot = createIcon(Flame);
const Rocket = createIcon(RocketIcon);
const SignalCellularAlt = createIcon(BarChart3);
const LocalLibrary = createIcon(Library);
const ArrowUpward = createIcon(ArrowUp);

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const GradientCard = styled(Card)(({ theme }) => ({
  height: '100%',
  minHeight: 440,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  backgroundColor: '#ffffff',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    '& .course-image': {
      transform: 'scale(1.02)',
    },
    '& .enroll-button': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'var(--education-rail-gradient, linear-gradient(90deg, #746EAC 0%, #3B9394 100%))',
    zIndex: 2,
  },
}));

// Stable constant arrays — defined once at module scope so their references
// never change between renders, keeping useMemo/React.memo deps stable.
const deliveryTypes = [
  { value: 'live-online', label: 'Live Online', icon: <VideoLabel />, color: '#8B5CF6' },
  { value: 'self-paced', label: 'Self-Paced', icon: <Computer />, color: '#10B981' },
  { value: 'in-person', label: 'In-Person', icon: <Class />, color: '#F59E0B' },
];

const levelOptions = [
  { value: 'beginner', label: 'Beginner', color: '#10B981' },
  { value: 'intermediate', label: 'Intermediate', color: '#F59E0B' },
  { value: 'advanced', label: 'Advanced', color: '#EF4444' },
  { value: 'expert', label: 'Expert', color: '#8B5CF6' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
];

const BackToTopButton = () => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const toggleVisibility = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  if (!visible) return null;
  return (
    <Button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        minWidth: 44,
        height: 44,
        borderRadius: '12px',
        backgroundColor: 'var(--education-primary, #746EAC)',
        color: 'white',
        boxShadow: '0 8px 24px var(--education-primary-muted, rgba(116,110,172,0.4))',
        '&:hover': {
          backgroundColor: 'var(--education-primary-dark, #6359A3)',
        },
        zIndex: 1000,
      }}
    >
      <ArrowUpward />
    </Button>
  );
};

const categoryIcons = {
  'Technology': <Computer sx={{ color: '#6366F1' }} />,
  'Business': <CastForEducation sx={{ color: '#10B981' }} />,
  'Design': <AutoStories sx={{ color: '#F59E0B' }} />,
  'Science': <School sx={{ color: '#8B5CF6' }} />,
  'Programming': <Computer sx={{ color: '#EC4899' }} />,
  'Marketing': <TrendingUp sx={{ color: '#3B82F6' }} />,
  'Finance': <SignalCellularAlt sx={{ color: '#059669' }} />,
  'default': <LocalLibrary sx={{ color: 'var(--education-primary, #746EAC)' }} />,
};

const getCategoryImageUrl = (category) => (
  category?.imageContent?.displayLink
    || category?.imageContent?.storedLink
    || category?.imageURL
    || ''
);

const getProductImageUrl = (product = {}) => {
  const contentImage = (Array.isArray(product.imageContents) ? product.imageContents : [])
    .map((image) => image?.displayLink || image?.previewUrl || '')
    .find(Boolean);
  const imageListUrl = (Array.isArray(product.imageUrlsList) ? product.imageUrlsList : [])
    .find((image) => typeof image === 'string' && image.trim());
  return contentImage
    || imageListUrl
    || product.imageURL
    || product.imageUrl
    || product.imageContent?.displayLink
    || product.imageContent?.previewUrl
    || '';
};

const allCoursesCategory = { primaryId: 'all', code: 'All', name: 'All Courses' };

const stripCategoryRuntimeFields = (category) => {
  if (!category || typeof category !== 'object') return category;
  const { icon, ...cacheableCategory } = category;
  return cacheableCategory;
};

const stripCategoryRuntimeFieldsList = (categoryList = []) => (
  (Array.isArray(categoryList) ? categoryList : []).map(stripCategoryRuntimeFields)
);

const withCategoryRuntimeFields = (category) => ({
  ...(category || {}),
  code: category?.code || category?.primaryId,
  icon: categoryIcons[category?.name] || categoryIcons.default,
});

const buildEnhancedCategories = (categoryList = []) => ([
  withCategoryRuntimeFields(allCoursesCategory),
  ...(Array.isArray(categoryList) ? categoryList : [])
    .filter((category) => String(category?.primaryId) !== 'all')
    .map(withCategoryRuntimeFields),
]);

const productIdentity = (product = {}) => product.primaryId || product._id || product.id || product.code || product.name;

// ---------------------------------------------------------------------------
// Pure helpers lifted out of the component body so their references are stable
// across renders. Defining these inside the component body creates a new
// function on every render, which breaks memoisation and triggers remounts of
// child components that depend on them.
// ---------------------------------------------------------------------------

const getDeliveryTypeInfo = (product) => {
  const deliveryType = (product.deliveryType || '').toUpperCase();
  const deliverySubType = (product.deliverySubType || '').toUpperCase();
  if (deliveryType.includes('VIRTUAL') || deliveryType.includes('ONLINE')) {
    if (deliverySubType.includes('INSTRUCTOR_LED') || deliverySubType.includes('LIVE')) {
      return {
        label: 'Live Online',
        value: 'live-online',
        color: '#8B5CF6',
        icon: <VideoLabel sx={{ fontSize: 12 }} />,
        bgColor: 'rgba(139, 92, 246, 0.1)',
      };
    }
    return {
      label: 'Self-Paced',
      value: 'self-paced',
      color: '#10B981',
      icon: <Computer sx={{ fontSize: 12 }} />,
      bgColor: 'rgba(16, 185, 129, 0.1)',
    };
  }
  if (
    deliveryType.includes('BRICK') ||
    deliveryType.includes('CLASSROOM') ||
    deliveryType.includes('PHYSICAL') ||
    deliveryType.includes('IN_PERSON')
  ) {
    return {
      label: 'In-Person',
      value: 'in-person',
      color: '#F59E0B',
      icon: <Class sx={{ fontSize: 12 }} />,
      bgColor: 'rgba(245, 158, 11, 0.1)',
    };
  }
  if (deliveryType.includes('HYBRID') || deliverySubType.includes('HYBRID')) {
    return {
      label: 'Hybrid',
      value: 'hybrid',
      color: '#EC4899',
      icon: <Computer sx={{ fontSize: 12 }} />,
      bgColor: 'rgba(236, 72, 153, 0.1)',
    };
  }
  return {
    label: 'Self-Paced',
    value: 'self-paced',
    color: 'var(--education-primary, #746EAC)',
    icon: <Computer sx={{ fontSize: 12 }} />,
    bgColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
  };
};

const getDifficultyLevel = (product) => {
  const level = (product.difficultyLevel || 'beginner').toLowerCase();
  const levels = {
    beginner: { label: 'Beginner', value: 'beginner', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    intermediate: { label: 'Intermediate', value: 'intermediate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' },
    advanced: { label: 'Advanced', value: 'advanced', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
    expert: { label: 'Expert', value: 'expert', color: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.1)' },
  };
  return levels[level] || levels.beginner;
};

const productSnapshot = (product) => {
  try {
    return JSON.stringify(product);
  } catch {
    return '';
  }
};

const reconcileProductList = (currentProducts = [], nextProducts = []) => {
  const currentById = new Map();
  currentProducts.forEach((product) => {
    const id = productIdentity(product);
    if (id != null) currentById.set(String(id), product);
  });

  let changed = currentProducts.length !== nextProducts.length;
  const reconciled = nextProducts.map((product, index) => {
    const id = productIdentity(product);
    const current = id != null ? currentById.get(String(id)) : null;
    if (current && productSnapshot(current) === productSnapshot(product)) {
      if (currentProducts[index] !== current) changed = true;
      return current;
    }
    if (currentProducts[index] !== product) changed = true;
    return product;
  });

  return changed ? reconciled : currentProducts;
};

const sameIdList = (first = [], second = []) => (
  first.length === second.length && first.every((id, index) => String(id) === String(second[index]))
);

const CourseCard = React.memo(({
  product,
  categories,
  partnerCompanies,
  onSelectProduct,
}) => {
  const categoryName = product.category?.name || 'Course';
  const categoryIcon = categoryIcons[categoryName] || categoryIcons.default;
  const category = categories.find((item) => String(item.primaryId) === String(product.category?.primaryId)) || product.category;
  const deliveryInfo = getDeliveryTypeInfo(product);
  const isInterview = isInterviewProduct(product);
  const isUpcoming = product.status === 'UPCOMING';
  const productCompanyId = Number(product.company?.primaryId || product.company?.companyId || 0);
  const productPartner = productCompanyId
    ? partnerCompanies.find((p) => p.primaryId === productCompanyId) || null
    : null;

  const handleEnrollNow = (e) => {
    e.stopPropagation();
    onSelectProduct(product);
  };

  const priceValue = product.price?.priceAmt || 0;
  const originalPriceValue = product.originalPrice || 0;
  const [isDescriptionExpanded, setDescriptionExpanded] = useState(false);
  const sanitizedDescription = (product.description?.replace(/<[^>]*>/g, '') || '').trim();
  const descriptionText = sanitizedDescription ||
    'Master essential skills with expert-led instruction and hands-on projects.';
  const isLongDescription = descriptionText.length > 100;
  const previewDescription = `${descriptionText.substring(0, 100)}...`;
  const courseImage = getProductImageUrl(product) || getCategoryImageUrl(category);

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <GradientCard>
        <Box sx={{
          position: 'relative',
          height: { xs: 190, sm: 210, md: 220 },
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}>
          {courseImage && (
            <CardMedia
              component="img"
              image={courseImage}
              alt={product.name}
              className="course-image"
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                boxSizing: 'border-box',
                p: 0,
                backgroundColor: '#ffffff',
                transition: 'transform 0.4s ease',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          {courseImage && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '28%',
                pointerEvents: 'none',
                background: 'linear-gradient(to top, rgba(0,0,0,0.22) 0%, transparent 100%)',
              }}
            />
          )}
          <Chip
            icon={categoryIcon}
            label={categoryName}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 24,
              borderRadius: '8px',
              '& .chip-icon': {
                marginLeft: '6px',
                fontSize: 14,
              },
            }}
          />
          {isUpcoming && (
            <Chip
              label="Available Soon"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                backgroundColor: '#1976d2',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 24,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            />
          )}
          {isInterview && (
            <Chip
              label="Interview"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: isUpcoming ? 12 : 12,
                backgroundColor: '#F59E0B',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 24,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            />
          )}
          {productPartner?.logoUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: isUpcoming ? 'auto' : 10,
                left: isUpcoming ? 10 : 'auto',
                width: 34,
                height: 34,
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.93)',
                padding: '3px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(3px)',
                zIndex: 3,
              }}
              title={`Powered by ${productPartner.name}`}
            >
              <img
                src={productPartner.logoUrl}
                alt={productPartner.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }}
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </Box>
          )}
        </Box>
        <CardContent sx={{
          p: 2.25,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: 264,
        }}>
          {productPartner && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: '#6B7280',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderRadius: '6px',
                  px: 0.75,
                  py: 0.25,
                  lineHeight: 1.4,
                }}
              >
                <SchoolIcon style={{ fontSize: 10 }} />
                Powered by {productPartner.name}
              </Typography>
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '0.98rem',
              lineHeight: 1.4,
              mb: 1.5,
              color: '#1F2937',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              height: '2.8em',
              minHeight: '2.8em',
            }}
          >
            {product.name?.length > 0
              ? product.name
              : 'Untitled Course'}
          </Typography>
          <Box sx={{ mb: 2, flex: 1, minHeight: '2.4em' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#6B7280',
                display: isDescriptionExpanded ? 'block' : '-webkit-box',
                WebkitLineClamp: isDescriptionExpanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.5,
                fontSize: '0.8rem',
              }}
            >
              {isDescriptionExpanded || !isLongDescription
                ? descriptionText
                : previewDescription}
            </Typography>
            {isLongDescription && (
              <Button
                size="small"
                onClick={() => setDescriptionExpanded((prev) => !prev)}
                sx={{
                  mt: 1,
                  p: 0,
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {isDescriptionExpanded ? 'Read less' : 'Read more'}
              </Button>
            )}
          </Box>
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={deliveryInfo.icon}
              label={deliveryInfo.label}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: deliveryInfo.bgColor,
                color: deliveryInfo.color,
                borderRadius: '8px',
                '& .chip-icon': {
                  marginLeft: '8px',
                  fontSize: 14,
                },
              }}
            />
          </Box>
          <Box sx={{
            mb: 2,
            borderBottom: '1px solid rgba(0,0,0,0.05)',
            pb: 2,
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box sx={{
                flex: 1,
                minWidth: 0,
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: 'var(--education-primary, #746EAC)',
                    fontSize: '1.25rem',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  <FormatByOperatingLocation
                    value={priceValue}
                    type="CURRENCY"
                  />
                </Typography>
                {originalPriceValue > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#9CA3AF',
                      textDecoration: 'line-through',
                      fontSize: '0.75rem',
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    <FormatByOperatingLocation
                      value={originalPriceValue}
                      type="CURRENCY"
                    />
                  </Typography>
                )}
              </Box>
              {originalPriceValue > 0 && originalPriceValue > priceValue && (
                <Chip
                  label={`Save ${Math.round(((originalPriceValue - priceValue) / originalPriceValue) * 100)}%`}
                  size="small"
                  sx={{
                    bgcolor: '#EF4444',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 20,
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 'auto' }}>
            <Button
              variant="contained"
              size="medium"
              fullWidth
              onClick={handleEnrollNow}
              className={`course-enroll-button${isUpcoming ? ' course-enroll-button-upcoming' : ''}`}
              data-testid="enroll-now-button"
              data-product-name={product.name}
              data-delivery-type={deliveryInfo.value}
              data-delivery-subtype={product.deliverySubType || 'none'}
              aria-label={`${isUpcoming ? 'View details of' : 'Enroll in'} ${product.name} - ${deliveryInfo.label}`}
              sx={{
                background: isUpcoming
                  ? 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)'
                  : 'var(--education-gradient, linear-gradient(135deg, #746EAC 0%, #8B5CF6 100%))',
                color: '#ffffff',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                py: 1.2,
                boxShadow: isUpcoming
                  ? '0 4px 12px rgba(107, 114, 128, 0.3)'
                  : '0 4px 12px var(--education-primary-shadow, rgba(116, 110, 172, 0.3))',
                '&:hover': {
                  background: isUpcoming
                    ? 'linear-gradient(135deg, #4B5563 0%, #6B7280 100%)'
                    : 'var(--education-gradient-hover, linear-gradient(135deg, #6359A3 0%, #746EAC 100%))',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isUpcoming ? 'View Details' : isInterview ? 'Choose Interviewer' : 'Enroll Now'}
            </Button>
          </Box>
        </CardContent>
      </GradientCard>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// FilterCard + FiltersPanel: defined OUTSIDE the Products component so React
// never assigns them a new component type on re-render.  All required state is
// passed explicitly as props, which lets React.memo bail out cheaply.
// ---------------------------------------------------------------------------

const FilterCard = styled(Paper)(() => ({
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  border: '1px solid #eef2ff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
}));

const FiltersPanel = React.memo(({
  searchInputRef,
  searchInputValue,
  statusFilter,
  categories,
  selectedCategories,
  expandedFilters,
  deliveryTypes,
  selectedDeliveryTypes,
  levelOptions,
  selectedLevels,
  partnerCompanies,
  companyPrimaryId,
  companyStateName,
  selectedProviders,
  onSearchChange,
  onClearSearch,
  onStatusChange,
  onCategoryChange,
  onToggleSection,
  onDeliveryChange,
  onLevelChange,
  onProviderChange,
  onReset,
}) => (
  <FilterCard sx={{ p: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75, color: '#1f2937' }}>
        <FilterAlt sx={{ fontSize: 14, color: 'var(--education-primary, #746eac)' }} />
        <span>Filters</span>
      </Typography>
      <Button
        size="small"
        onClick={onReset}
        sx={{ textTransform: 'none', color: '#6b7280', fontSize: '0.7rem', fontWeight: 500, minWidth: 'auto', p: 0.5 }}
      >
        Clear All
      </Button>
    </Box>
    <TextField
      inputRef={searchInputRef}
      fullWidth
      placeholder="Search courses..."
      value={searchInputValue}
      onChange={onSearchChange}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search sx={{ color: '#9ca3af', fontSize: 14 }} />
          </InputAdornment>
        ),
        endAdornment: searchInputValue && (
          <IconButton size="small" onClick={onClearSearch} sx={{ p: 0.25 }}>
            <Close sx={{ fontSize: 12 }} />
          </IconButton>
        ),
      }}
      sx={{
        mb: 1.5,
        '& .input-root': { fontSize: '0.8rem' },
        '& .input': { padding: '6px 8px', fontSize: '0.8rem' },
      }}
    />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem', mb: 0.75, display: 'block' }}>
        Status
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={statusFilter}
          onChange={onStatusChange}
          sx={{ fontSize: '0.75rem', '& .input-outline': { borderColor: '#e5e7eb' } }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Courses</MenuItem>
          <MenuItem value="upcoming" sx={{ fontSize: '0.75rem' }}>Available Soon</MenuItem>
        </Select>
      </FormControl>
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, cursor: 'pointer', p: 0.5, borderRadius: '4px' }}
        onClick={() => onToggleSection('categories')}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>
          Categories
        </Typography>
        {expandedFilters.categories
          ? <ExpandLess sx={{ fontSize: 14, color: '#9ca3af' }} />
          : <ExpandMore sx={{ fontSize: 14, color: '#9ca3af' }} />}
      </Box>
      <Collapse in={expandedFilters.categories}>
        <FormGroup sx={{ maxHeight: 160, overflowY: 'auto', pr: 0.5 }}>
          {categories.slice(0, 8).map((category) => (
            <FormControlLabel
              key={category.primaryId}
              control={
                <Checkbox
                  checked={selectedCategories.includes(category.primaryId)}
                  onChange={() => onCategoryChange(category.primaryId)}
                  size="small"
                  sx={{ color: '#d1d5db', padding: '3px' }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                  <Box sx={{ color: 'var(--education-primary, #746eac)', fontSize: '0.75rem' }}>
                    {category.icon}
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '0.75rem',
                    fontWeight: selectedCategories.includes(category.primaryId) ? 600 : 400,
                    color: selectedCategories.includes(category.primaryId) ? '#1f2937' : '#6b7280',
                  }}>
                    {category.name}
                  </Typography>
                </Box>
              }
              sx={{ mb: 0.25, ml: 0 }}
            />
          ))}
        </FormGroup>
      </Collapse>
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, cursor: 'pointer', p: 0.5, borderRadius: '4px' }}
        onClick={() => onToggleSection('delivery')}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>
          Learning Format
        </Typography>
        {expandedFilters.delivery
          ? <ExpandLess sx={{ fontSize: 14, color: '#9ca3af' }} />
          : <ExpandMore sx={{ fontSize: 14, color: '#9ca3af' }} />}
      </Box>
      <Collapse in={expandedFilters.delivery}>
        <FormGroup>
          {deliveryTypes.map((type) => (
            <FormControlLabel
              key={type.value}
              control={
                <Checkbox
                  checked={selectedDeliveryTypes.includes(type.value)}
                  onChange={(e) => {
                    if (e.target.checked) onDeliveryChange((prev) => [...prev, type.value]);
                    else onDeliveryChange((prev) => prev.filter((t) => t !== type.value));
                  }}
                  size="small"
                  sx={{ color: '#d1d5db', padding: '3px' }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                  <Box sx={{ color: type.color, fontSize: '0.75rem' }}>{type.icon}</Box>
                  <Typography variant="caption" sx={{
                    fontSize: '0.75rem',
                    fontWeight: selectedDeliveryTypes.includes(type.value) ? 600 : 400,
                    color: selectedDeliveryTypes.includes(type.value) ? '#1f2937' : '#6b7280',
                  }}>
                    {type.label}
                  </Typography>
                </Box>
              }
              sx={{ mb: 0.25, ml: 0 }}
            />
          ))}
        </FormGroup>
      </Collapse>
    </Box>
    <Box>
      <Collapse in={expandedFilters.level}>
        <FormGroup>
          {levelOptions.map((level) => (
            <FormControlLabel
              key={level.value}
              control={
                <Checkbox
                  checked={selectedLevels.includes(level.value)}
                  onChange={(e) => {
                    if (e.target.checked) onLevelChange((prev) => [...prev, level.value]);
                    else onLevelChange((prev) => prev.filter((l) => l !== level.value));
                  }}
                  size="small"
                  sx={{ color: '#d1d5db', padding: '3px' }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: level.color }} />
                  <Typography variant="caption" sx={{
                    fontSize: '0.75rem',
                    fontWeight: selectedLevels.includes(level.value) ? 600 : 400,
                    color: selectedLevels.includes(level.value) ? '#1f2937' : '#6b7280',
                  }}>
                    {level.label}
                  </Typography>
                </Box>
              }
              sx={{ mb: 0.25, ml: 0 }}
            />
          ))}
        </FormGroup>
      </Collapse>
    </Box>
    {partnerCompanies.length > 0 && (
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, cursor: 'pointer', p: 0.5, borderRadius: '4px' }}
          onClick={() => onToggleSection('providers')}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>
            Powered By
          </Typography>
          {expandedFilters.providers
            ? <ExpandLess sx={{ fontSize: 14, color: '#9ca3af' }} />
            : <ExpandMore sx={{ fontSize: 14, color: '#9ca3af' }} />}
        </Box>
        <Collapse in={expandedFilters.providers}>
          <FormGroup>
            {[
              { primaryId: Number(companyPrimaryId) || 0, name: companyStateName },
              ...partnerCompanies,
            ].filter((p) => p.primaryId > 0).map((provider) => (
              <FormControlLabel
                key={provider.primaryId}
                control={
                  <Checkbox
                    checked={selectedProviders.includes(provider.primaryId)}
                    onChange={(e) => {
                      if (e.target.checked) onProviderChange((prev) => [...prev, provider.primaryId]);
                      else onProviderChange((prev) => prev.filter((id) => id !== provider.primaryId));
                    }}
                    size="small"
                    sx={{ color: '#d1d5db', padding: '3px' }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                    <Box sx={{ color: 'var(--education-primary, #746eac)', fontSize: '0.75rem', display: 'flex' }}>
                      <SchoolIcon style={{ fontSize: 13 }} />
                    </Box>
                    <Typography variant="caption" sx={{
                      fontSize: '0.75rem',
                      fontWeight: selectedProviders.includes(provider.primaryId) ? 600 : 400,
                      color: selectedProviders.includes(provider.primaryId) ? '#1f2937' : '#6b7280',
                    }}>
                      {provider.name}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 0.25, ml: 0 }}
              />
            ))}
          </FormGroup>
        </Collapse>
      </Box>
    )}
  </FilterCard>
));

const Products = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchInputRef = useRef(null);
  const allProductsRef = useRef([]);       // full list (parent + all partners) — source of truth when no subset filter active
  const allProviderIdsRef = useRef([]);    // all provider IDs (parent + partners) — used to detect "all selected" vs subset
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerLoading, setProviderLoading] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDeliveryTypes, setSelectedDeliveryTypes] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedFilters, setExpandedFilters] = useState({
    categories: true,
    delivery: true,
    level: true,
    providers: true,
  });
  const companyState = useSelector((state) => state.companyReducer.company) || {};
  const companyPrimaryId = companyState?.primaryId || companyState?.companyId || '';
  const productsCacheKey = useMemo(
    () => getEducationPageCacheKey('products', { companyId: companyPrimaryId }),
    [companyPrimaryId]
  );

  // Phase 1: Discover partner companies by parentCompanyId — separate from product fetch.
  // Stored as {primaryId, name} objects (stable IDs only) for badges/filter UI.
  const [partnerCompanies, setPartnerCompanies] = useState([]);
  const [partnersReady, setPartnersReady] = useState(false);

  const setVisibleProducts = useCallback((nextProducts) => {
    setProducts((currentProducts) => reconcileProductList(currentProducts, nextProducts));
  }, []);

  const setCatalogProducts = useCallback((nextProducts) => {
    setProducts((currentProducts) => {
      const reconciled = reconcileProductList(currentProducts, nextProducts);
      allProductsRef.current = reconciled;
      return reconciled;
    });
  }, []);
  const setStableCategories = useCallback((nextCategories) => {
    setCategories((currentCategories) => reconcileProductList(currentCategories, nextCategories));
  }, []);
  const setStablePartnerCompanies = useCallback((nextPartners) => {
    setPartnerCompanies((currentPartners) => reconcileProductList(currentPartners, nextPartners));
  }, []);

  useEffect(() => {
    if (!companyPrimaryId) return;
    const cached = readEducationPageCache(productsCacheKey);
    if (cached) {
      const cachedProducts = cached.products || [];
      const cachedCategories = buildEnhancedCategories(cached.categories || []);
      const cachedPartners = cached.partnerCompanies || [];

      allProductsRef.current = cachedProducts;
      setVisibleProducts(cachedProducts);
      if (cachedCategories.length) setStableCategories(cachedCategories);
      setStablePartnerCompanies(cachedPartners);
      const allIds = [Number(companyPrimaryId), ...cachedPartners.map((p) => p.primaryId)].filter(Boolean);
      allProviderIdsRef.current = allIds;
      setSelectedProviders((current) => {
        const next = [Number(companyPrimaryId)].filter(Boolean);
        return sameIdList(current, next) ? current : next;
      });
      setLoading(false);
    }

    if (readEducationPageCache(productsCacheKey, { maxAgeMs: PRODUCTS_CACHE_MAX_AGE_MS })?.partnerCompanies) {
      setPartnersReady(true);
    }

    setPartnersReady(Boolean(cached));
    dispatch(findPartnerCompanies(companyPrimaryId))
      .then((list) => {
        const partners = (Array.isArray(list) ? list : [])
          // An inactive partner no longer has courses in the merged catalog, so listing
          // it under "Powered By" would offer a provider facet that returns nothing.
          .filter((p) => String(p?.status || '').toUpperCase() !== 'INACTIVE')
          .map((p) => ({
            primaryId: Number(p.primaryId || p.companyId || 0),
            name: p.name || p.companyName || 'Partner',
            logoUrl: p.logoImageContent?.displayLink
              || p.logoImageContent?.storedLink
              || p.logoLocation
              || '',
          }))
          .filter((p) => p.primaryId > 0);
        setStablePartnerCompanies(partners);

        // Track all provider IDs for "all selected" detection in the filter effect.
        // Default selection is the parent company only — the backend merges partner data.
        const allIds = [Number(companyPrimaryId), ...partners.map((p) => p.primaryId)].filter(Boolean);
        allProviderIdsRef.current = allIds;
        setSelectedProviders((current) => {
          const next = [Number(companyPrimaryId)].filter(Boolean);
          return sameIdList(current, next) ? current : next;
        });
        writeEducationPageCache(productsCacheKey, {
          ...(readEducationPageCache(productsCacheKey) || {}),
          partnerCompanies: partners,
        });
      })
      .catch(() => {})
      .finally(() => setPartnersReady(true));
  }, [dispatch, companyPrimaryId, productsCacheKey, setStableCategories, setStablePartnerCompanies, setVisibleProducts]);

  // Phase 2: Fetch products for parent + all partners in one batch.
  // Waits until partner discovery is done so both sets appear on the first render.
  const fetchProducts = useCallback(async () => {
    if (!companyPrimaryId || !partnersReady) return;
    const hasCachedProducts = allProductsRef.current.length > 0;
    setLoading(!hasCachedProducts);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const searchTerm = urlParams.get('category') || '';

      // Query the parent company only — the backend merges partner data automatically.
      const companyIdsToFetch = [companyPrimaryId].filter(Boolean);

      const allFetchResults = await Promise.all(companyIdsToFetch.map(async (cid) => {
        const [allResult, validResult, upcomingResult, interviewResult] = await Promise.all([
          dispatch(getAllValidProducts(cid, searchTerm)).catch(() => null),
          dispatch(getValidProducts(cid, searchTerm)).catch(() => null),
          dispatch(getUpcomingProducts(cid, searchTerm)).catch(() => null),
          dispatch(getInterviewProducts(cid, searchTerm)).catch(() => null),
        ]);
        return [allResult, validResult, upcomingResult, interviewResult];
      }));

      const seenIds = new Set();
      const allProducts = [];
      for (const [productsResult, validProductsResult, upcomingResult, interviewResult] of allFetchResults) {
        const arrayAll = Array.isArray(productsResult?.data) ? productsResult.data : Array.isArray(productsResult) ? productsResult : [];
        const arrayValid = Array.isArray(validProductsResult?.data) ? validProductsResult.data : Array.isArray(validProductsResult) ? validProductsResult : [];
        const arrayUpcoming = Array.isArray(upcomingResult?.data) ? upcomingResult.data : Array.isArray(upcomingResult) ? upcomingResult : [];
        const arrayInterview = (Array.isArray(interviewResult?.data) ? interviewResult.data : Array.isArray(interviewResult) ? interviewResult : [])
          .filter(isInterviewProduct);

        const merged = [...arrayAll, ...arrayValid.filter((p) => !arrayAll.some((ap) => ap.primaryId === p.primaryId))];
        const full = [...merged, ...arrayUpcoming.filter((up) => !merged.some((vp) => vp.primaryId === up.primaryId))];
        const withInterviews = [...full, ...arrayInterview.filter((interview) => !full.some((product) => product.primaryId === interview.primaryId))];

        for (const p of withInterviews) {
          if (!seenIds.has(p.primaryId)) {
            seenIds.add(p.primaryId);
            allProducts.push(p);
          }
        }
      }

      // Only update state + cache when data actually changed.
      // reconcileProductList handles object-level identity; here we additionally
      // guard the cache write so a no-op API response doesn't trigger a re-render.
      const prevIds = allProductsRef.current.map((p) => p.primaryId).join(',');
      const nextIds = allProducts.map((p) => p.primaryId).join(',');
      const hasNewContent = prevIds !== nextIds
        || allProducts.some((p, i) => productSnapshot(p) !== productSnapshot(allProductsRef.current[i]));

      if (hasNewContent) {
        setCatalogProducts(allProducts);
        writeEducationPageCache(productsCacheKey, {
          ...(readEducationPageCache(productsCacheKey) || {}),
          products: allProducts,
        });
      }
    } catch {
      if (allProductsRef.current.length === 0) setVisibleProducts([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, companyPrimaryId, partnersReady, productsCacheKey, setCatalogProducts, setVisibleProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // When a "Powered By" provider is selected, make a real API call for that company's products.
  // When cleared, restore the full merged list from allProductsRef.
  const fetchProductsForProviders = useCallback(async (providerIds) => {
    if (!providerIds.length) {
      setVisibleProducts(allProductsRef.current);
      return;
    }
    setProviderLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const searchTerm = urlParams.get('category') || '';

        const allFetchResults = await Promise.all(
          providerIds.map((cid) =>
            Promise.all([
              dispatch(getAllValidProducts(cid, searchTerm)),
              dispatch(getValidProducts(cid, searchTerm)),
              dispatch(getUpcomingProducts(cid, searchTerm)),
              dispatch(getInterviewProducts(cid, searchTerm)),
            ])
          )
        );

      const seenIds = new Set();
      const providerProducts = [];
      for (const [productsResult, validProductsResult, upcomingResult, interviewResult] of allFetchResults) {
        const arrayAll = Array.isArray(productsResult?.data) ? productsResult.data : Array.isArray(productsResult) ? productsResult : [];
        const arrayValid = Array.isArray(validProductsResult?.data) ? validProductsResult.data : Array.isArray(validProductsResult) ? validProductsResult : [];
        const arrayUpcoming = Array.isArray(upcomingResult?.data) ? upcomingResult.data : Array.isArray(upcomingResult) ? upcomingResult : [];
        const arrayInterview = (Array.isArray(interviewResult?.data) ? interviewResult.data : Array.isArray(interviewResult) ? interviewResult : [])
          .filter(isInterviewProduct);
        const merged = [...arrayAll, ...arrayValid.filter((p) => !arrayAll.some((ap) => ap.primaryId === p.primaryId))];
        const full = [...merged, ...arrayUpcoming.filter((up) => !merged.some((vp) => vp.primaryId === up.primaryId))];
        const withInterviews = [...full, ...arrayInterview.filter((interview) => !full.some((product) => product.primaryId === interview.primaryId))];
        for (const p of withInterviews) {
          if (!seenIds.has(p.primaryId)) {
            seenIds.add(p.primaryId);
            providerProducts.push(p);
          }
        }
      }
      setVisibleProducts(providerProducts);
    } catch {
      setVisibleProducts(allProductsRef.current);
    } finally {
      setProviderLoading(false);
    }
  }, [dispatch, setVisibleProducts]);

  useEffect(() => {
    const total = allProviderIdsRef.current.length;
    const selected = selectedProviders.length;
    // Parent company selected alone means "show everything" — backend already merged partners.
    const onlyParentSelected = selected === 1 && selectedProviders[0] === Number(companyPrimaryId);

    // "All ticked", "none selected", or only parent selected → restore full cached list
    if (!selected || onlyParentSelected || (total > 0 && selected >= total)) {
      if (allProductsRef.current.length) setVisibleProducts(allProductsRef.current);
      return;
    }

    // Specific partner(s) selected → live API call scoped to those providers
    fetchProductsForProviders(selectedProviders);
  }, [selectedProviders, fetchProductsForProviders, companyPrimaryId, setVisibleProducts]);

  useEffect(() => {
    if (!companyPrimaryId) return;

    const fetchCategories = async () => {
      try {
        const categoriesResult = await dispatch(getAllProductCategories(companyPrimaryId));
        const enhancedCategories = [
          ...buildEnhancedCategories(categoriesResult || []),
        ];
        setStableCategories(enhancedCategories);
        writeEducationPageCache(productsCacheKey, {
          ...(readEducationPageCache(productsCacheKey) || {}),
          categories: stripCategoryRuntimeFieldsList(categoriesResult || []),
        });

        // Pre-select the category from the URL query param (?category=<primaryId>).
        // Normalise to a number so it matches product.category.primaryId which is
        // always numeric from the API.
        const categoryId = getCategoryParamId();
        if (categoryId) setSelectedCategories([categoryId]);
      } catch {
        setStableCategories(buildEnhancedCategories());
      }
    };
    const cached = readEducationPageCache(productsCacheKey);
    if (cached?.categories?.length) setStableCategories(buildEnhancedCategories(cached.categories));
    const categoryId = getCategoryParamId();
    if (categoryId) setSelectedCategories([categoryId]);
    fetchCategories();
  }, [dispatch, companyPrimaryId, productsCacheKey, setStableCategories]);

  const debouncedSearch = useMemo(
    () => debounce((query) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Filter products based on all criteria, including status filter
  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (statusFilter !== 'all') {
      result = result.filter(product => product.status === statusFilter.toUpperCase());
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.name?.toLowerCase().includes(query)
      );
    }
    if (selectedCategories.length > 0 && !selectedCategories.includes('all')) {
      result = result.filter(product =>
        product.category?.primaryId != null &&
        selectedCategories.some(id => String(id) === String(product.category.primaryId))
      );
    }
    if (selectedDeliveryTypes.length > 0) {
      result = result.filter(product => {
        const deliveryInfo = getDeliveryTypeInfo(product);
        return selectedDeliveryTypes.includes(deliveryInfo.value);
      });
    }
    if (selectedLevels.length > 0) {
      result = result.filter(product => {
        const levelInfo = getDifficultyLevel(product);
        return selectedLevels.includes(levelInfo.value);
      });
    }
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.price?.priceAmt || 0) - (b.price?.priceAmt || 0));
        break;
      case 'price-high':
        result.sort((a, b) => (b.price?.priceAmt || 0) - (a.price?.priceAmt || 0));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'popular':
        result.sort((a, b) => (b.enrollmentCount || 0) - (a.enrollmentCount || 0));
        break;
      default:
        result.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
        break;
    }
    return result;
  }, [products, searchQuery, selectedCategories, selectedDeliveryTypes, selectedLevels, sortBy, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / COURSES_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * COURSES_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + COURSES_PER_PAGE);
  }, [currentPage, filteredProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategories, selectedDeliveryTypes, selectedLevels, selectedProviders, sortBy, statusFilter]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handleResetFilters = useCallback(() => {
    setSelectedCategories([]);
    setSelectedDeliveryTypes([]);
    setSelectedLevels([]);
    setSelectedProviders(allProviderIdsRef.current);
    setSortBy('newest');
    setSearchQuery('');
    setSearchInputValue('');
    setStatusFilter('all');
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchInputValue('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const toggleFilterSection = useCallback((section) => {
    setExpandedFilters(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleCategoryChange = useCallback((categoryPrimaryId) => {
    setSelectedCategories(prev => {
      if (categoryPrimaryId === 'all') {
        return prev.includes('all') ? [] : ['all'];
      }
      // Normalise to number so sidebar selection and URL pre-selection stay in sync.
      const normId = Number(categoryPrimaryId) || categoryPrimaryId;
      const isSelected = prev.some(id => String(id) === String(normId));
      if (isSelected) {
        return prev.filter(id => String(id) !== String(normId));
      }
      return [...prev.filter(c => c !== 'all'), normId];
    });
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleSelectProduct = useCallback((product) => {
    dispatch(setSelectedProduct(product));
    navigate('/course-detail');
  }, [dispatch, navigate]);

  // deliveryTypes, levelOptions, sortOptions are defined at module scope below
  // the component to keep their references stable across renders.

  // Stable props bundle passed to the module-level <FiltersPanel> component.
  // Wrapping in useMemo means the bundle object is only replaced when actual
  // filter state changes, which is what keeps FiltersPanel from re-rendering
  // for unrelated state changes inside Products.
  const filterPanelProps = useMemo(() => ({
    searchInputRef,
    searchInputValue,
    statusFilter,
    categories,
    selectedCategories,
    expandedFilters,
    deliveryTypes,
    selectedDeliveryTypes,
    levelOptions,
    selectedLevels,
    partnerCompanies,
    companyPrimaryId,
    companyStateName: companyState?.name || 'Main Catalog',
    selectedProviders,
    onSearchChange: handleSearchInputChange,
    onClearSearch: handleClearSearch,
    onStatusChange: handleStatusFilterChange,
    onCategoryChange: handleCategoryChange,
    onToggleSection: toggleFilterSection,
    onDeliveryChange: setSelectedDeliveryTypes,
    onLevelChange: setSelectedLevels,
    onProviderChange: setSelectedProviders,
    onReset: handleResetFilters,
  }), [
    searchInputValue, statusFilter, categories, selectedCategories, expandedFilters,
    selectedDeliveryTypes, selectedLevels, partnerCompanies, companyPrimaryId,
    companyState?.name, selectedProviders,
    handleSearchInputChange, handleClearSearch, handleStatusFilterChange,
    handleCategoryChange, toggleFilterSection, handleResetFilters,
  ]);

  if (loading && products.length === 0) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: '3px solid var(--education-primary-muted, rgba(116, 110, 172, 0.2))',
              borderTopColor: 'var(--education-primary, #746EAC)',
              animation: 'spin 1s linear infinite',
              mb: 3,
              mx: 'auto',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
            Loading Courses
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            We're preparing your learning journey
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      // The header is fixed/overlayed on education routes. Keep the first
      // course row below it in both template and fallback render paths.
      paddingTop: 'calc(var(--layout-header-height, 70px) + 24px)',
      background: 'linear-gradient(135deg, var(--education-background, #F9FAFB) 0%, var(--education-surface, #F3F4F6) 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          height: '100%',
          background: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Container maxWidth="xl" sx={{ py: 2, maxWidth: 'none', width: '100%', boxSizing: 'border-box', paddingLeft: '24px', paddingRight: '24px' }}>
        <Box className="products-layout">
          {!isMobile && (
            <Box className="products-sidebar">
              <FiltersPanel {...filterPanelProps} />
            </Box>
          )}
          <Box className="products-results">
            <Box sx={{
              mb: 4,
              backgroundColor: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              border: '1px solid var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1F2937', mb: 0.5 }}>
                    {statusFilter === 'upcoming' ? 'Coming Soon' : 'Featured Courses'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Whatshot sx={{ fontSize: 16, color: '#EF4444' }} />
                    {statusFilter === 'upcoming'
                      ? 'Courses launching soon – be the first to know'
                      : 'Best courses to elevate your skills'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isMobile && (
                    <Button
                      variant="outlined"
                      onClick={() => setFilterDrawerOpen(true)}
                      size="small"
                      startIcon={<FilterAlt />}
                      sx={{
                        borderColor: 'var(--education-primary-muted, rgba(116, 110, 172, 0.2))',
                        color: 'var(--education-primary, #746EAC)',
                        borderRadius: '12px',
                        fontWeight: 600,
                        px: 2,
                        '&:hover': {
                          borderColor: 'var(--education-primary, #746EAC)',
                          backgroundColor: 'var(--education-primary-subtle, rgba(116, 110, 172, 0.05))',
                        },
                      }}
                    >
                      Filters
                    </Button>
                  )}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        '& .input-outline': {
                          borderColor: 'var(--education-primary-muted, rgba(116, 110, 172, 0.2))',
                        },
                        '&:hover .input-outline': {
                          borderColor: 'var(--education-primary, #746EAC)',
                        },
                      }}
                      IconComponent={KeyboardArrowDown}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.value === 'newest' && <Rocket sx={{ fontSize: 16, color: '#8B5CF6' }} />}
                            {option.value === 'price-low' && <ArrowForward sx={{ fontSize: 16, color: '#10B981' }} />}
                            {option.value === 'price-high' && <ArrowForward sx={{ fontSize: 16, color: '#EF4444', transform: 'rotate(180deg)' }} />}
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{
                    display: 'flex',
                    border: '1px solid var(--education-primary-muted, rgba(116, 110, 172, 0.2))',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('grid')}
                      sx={{
                        backgroundColor: viewMode === 'grid' ? 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))' : 'transparent',
                        color: viewMode === 'grid' ? 'var(--education-primary, #746EAC)' : '#6B7280',
                        borderRadius: 0,
                        padding: 0.75,
                        borderRight: '1px solid var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                        '&:hover': {
                          backgroundColor: 'var(--education-primary-subtle, rgba(116, 110, 172, 0.05))',
                        },
                      }}
                    >
                      <ViewModule />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('list')}
                      sx={{
                        backgroundColor: viewMode === 'list' ? 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))' : 'transparent',
                        color: viewMode === 'list' ? 'var(--education-primary, #746EAC)' : '#6B7280',
                        borderRadius: 0,
                        padding: 0.75,
                        '&:hover': {
                          backgroundColor: 'var(--education-primary-subtle, rgba(116, 110, 172, 0.05))',
                        },
                      }}
                    >
                      <ViewList />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
              {(selectedCategories.length > 0 || selectedDeliveryTypes.length > 0 || selectedLevels.length > 0 || selectedProviders.length > 0 || searchQuery) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {searchQuery && (
                    <Chip
                      label={`"${searchQuery}"`}
                      onDelete={handleClearSearch}
                      size="small"
                      sx={{
                        backgroundColor: 'var(--education-primary, #746EAC)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: '8px',
                        height: 28,
                        '& .chip-delete-icon': {
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: 16,
                        },
                      }}
                    />
                  )}
                  {selectedCategories.filter(c => c !== 'all').map(catId => {
                    const category = categories.find(c => c.primaryId === catId);
                    return category && (
                      <Chip
                        key={catId}
                        icon={category.icon}
                        label={category.name}
                        onDelete={() => setSelectedCategories(prev => prev.filter(c => c !== catId))}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                          color: 'var(--education-primary, #746EAC)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          height: 28,
                          '& .chip-icon': {
                            color: 'var(--education-primary, #746EAC)',
                            fontSize: 14,
                            marginLeft: 1,
                          },
                          '& .chip-delete-icon': {
                            color: '#9CA3AF',
                            fontSize: 16,
                          },
                        }}
                      />
                    );
                  })}
                  {selectedDeliveryTypes.map(type => {
                    const delivery = deliveryTypes.find(d => d.value === type);
                    return delivery && (
                      <Chip
                        key={type}
                        icon={delivery.icon}
                        label={delivery.label}
                        onDelete={() => setSelectedDeliveryTypes(prev => prev.filter(t => t !== type))}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                          color: delivery.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          height: 28,
                          '& .chip-icon': {
                            color: delivery.color,
                            fontSize: 14,
                            marginLeft: 1,
                          },
                          '& .chip-delete-icon': {
                            color: '#9CA3AF',
                            fontSize: 16,
                          },
                        }}
                      />
                    );
                  })}
                  {selectedLevels.map(level => {
                    const levelInfo = levelOptions.find(l => l.value === level);
                    return levelInfo && (
                      <Chip
                        key={level}
                        label={levelInfo.label}
                        onDelete={() => setSelectedLevels(prev => prev.filter(l => l !== level))}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                          color: levelInfo.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          height: 28,
                          '& .chip-delete-icon': {
                            color: '#9CA3AF',
                            fontSize: 16,
                          },
                        }}
                      />
                    );
                  })}
                  {selectedProviders.map(providerId => {
                    const allProviders = [
                      { primaryId: Number(companyPrimaryId) || 0, name: companyState?.name || 'Main Catalog' },
                      ...partnerCompanies,
                    ];
                    const provider = allProviders.find(p => p.primaryId === providerId);
                    return provider && (
                      <Chip
                        key={providerId}
                        label={`Powered by ${provider.name}`}
                        onDelete={() => setSelectedProviders(prev => prev.filter(id => id !== providerId))}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                          color: 'var(--education-primary, #746EAC)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          height: 28,
                          '& .chip-delete-icon': {
                            color: '#9CA3AF',
                            fontSize: 16,
                          },
                        }}
                      />
                    );
                  })}
                  <Button
                    size="small"
                    onClick={handleResetFilters}
                    sx={{
                      textTransform: 'none',
                      color: '#6B7280',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      ml: 'auto',
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Box>
            {providerLoading ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '1px solid var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                mb: 3,
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '3px solid var(--education-primary-muted, rgba(116, 110, 172, 0.2))',
                    borderTopColor: 'var(--education-primary, #746EAC)',
                    animation: 'spin 1s linear infinite',
                    mb: 1.5,
                    mx: 'auto',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }} />
                  <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
                    Loading courses...
                  </Typography>
                </Box>
              </Box>
            ) : filteredProducts.length > 0 ? (
              <Box className={`course-grid${viewMode === 'list' ? ' course-grid-list' : ''}`}>
                {paginatedProducts.map((product) => (
                  <Box className="course-grid-item" key={product.primaryId}>
                    <CourseCard
                      product={product}
                      categories={categories}
                      partnerCompanies={partnerCompanies}
                      onSelectProduct={handleSelectProduct}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{
                textAlign: 'center',
                py: 8,
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '1px solid var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                mb: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}>
                  <Search sx={{ fontSize: 40, color: 'var(--education-primary, #746EAC)' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 1.5, color: '#1F2937', fontWeight: 700 }}>
                  No courses found
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: '#6B7280', maxWidth: 400, mx: 'auto' }}>
                  Try adjusting your filters or search for something else. We have thousands of courses waiting for you!
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleResetFilters}
                    sx={{
                      background: 'var(--education-gradient, linear-gradient(135deg, #746EAC 0%, #8B5CF6 100%))',
                      color: '#ffffff',
                      px: 3,
                      py: 1,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 12px var(--education-primary-shadow, rgba(116, 110, 172, 0.3))',
                      '&:hover': {
                        background: 'var(--education-gradient-hover, linear-gradient(135deg, #6359A3 0%, #746EAC 100%))',
                        boxShadow: '0 6px 20px var(--education-primary-glow, rgba(116, 110, 172, 0.4))',
                      },
                    }}
                  >
                    Reset All Filters
                  </Button>
                </Box>
              </Box>
            )}
            {filteredProducts.length > 0 && (
              <Box className="course-pagination" aria-label="Course pagination">
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.8rem' }}>
                  Showing {((currentPage - 1) * COURSES_PER_PAGE) + 1}-{Math.min(currentPage * COURSES_PER_PAGE, filteredProducts.length)} of {filteredProducts.length} courses
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Button
                    aria-label="Previous page"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '10px', borderColor: '#E5E7EB', color: '#374151', backgroundColor: '#FFFFFF' }}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <Button
                      key={page}
                      aria-label={`Go to page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                      onClick={() => setCurrentPage(page)}
                      sx={{
                        minWidth: 36,
                        width: 36,
                        height: 36,
                        p: 0,
                        borderRadius: '10px',
                        borderColor: currentPage === page ? 'var(--education-primary, #746EAC)' : '#E5E7EB',
                        backgroundColor: currentPage === page ? 'var(--education-primary, #746EAC)' : '#FFFFFF',
                        color: currentPage === page ? '#FFFFFF' : '#374151',
                        fontWeight: currentPage === page ? 700 : 500,
                      }}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    aria-label="Next page"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '10px', borderColor: '#E5E7EB', color: '#374151', backgroundColor: '#FFFFFF' }}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
      <Box
        sx={{
          position: "relative",
          mt: 10,
          py: { xs: 8, md: 10 },
          overflow: "hidden",
          backgroundColor: "#0B0F1A",
        }}
      >
        <motion.div
          animate={{ x: [0, 60, 0], y: [0, -30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "12%",
            left: "18%",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, var(--education-primary-shadow, rgba(116,110,172,0.28)), transparent 70%)",
            filter: "blur(50px)",
          }}
        />
        <motion.div
          animate={{ x: [0, -70, 0], y: [0, 40, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "12%",
            right: "14%",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(56,189,248,0.22), transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            viewport={{ once: true }}
            style={{
              backdropFilter: "blur(16px)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.13), rgba(255,255,255,0.05))",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 22,
              padding: "44px 40px",
              textAlign: "center",
              boxShadow: "0 28px 80px rgba(0,0,0,0.4)",
            }}
          >
            <Typography
              sx={{
                fontFamily: `"Source Serif 4", "Georgia", serif`,
                fontSize: { xs: 19, md: 22 },
                fontWeight: 500,
                lineHeight: 1.55,
                letterSpacing: "-0.01em",
                color: "rgba(255,255,255,0.95)",
                maxWidth: 560,
                mx: "auto",
                mb: 4,
              }}
            >
              "Learn. Apply. Repeat. Education rewards the rhythm."
            </Typography>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 64 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              style={{
                height: 2,
                margin: "0 auto 24px",
                background:
                  "linear-gradient(90deg, transparent, #A78BFA, transparent)",
              }}
            />
            <motion.div
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.96 }}
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                color: "rgba(255,255,255,0.7)",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Back to top
              <motion.span
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 1.8 }}
              >
                ↑
              </motion.span>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
      {filterDrawerOpen && (
        <Drawer
          anchor="left"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{ sx: { width: 280, p: 2, backgroundColor: '#ffffff' } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>Filters</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <FiltersPanel {...filterPanelProps} />
        </Drawer>
      )}
      <BackToTopButton />
    </Box>
  );
};

export default Products;
